import { Injectable } from "@angular/core";

import { environment as env } from "src/environments/environment";

export interface GeneralConfig {
    apiBaseUrl: string;
    ssoTokenKey: string;
    localTokenKey: string;
    /**
     * Dove il frontend conserva la propria copia delle informazioni di login.
     *
     * `localstorage` è la modalità di questo prodotto. Il payload contiene l'elenco delle AUTH
     * dell'utente (~3,5 KB per un SUPER_ADMIN) e nel cookie era al 98% del limite di 4096 byte
     * imposto dai browser: superato quel limite il cookie viene scartato in SILENZIO e l'utente
     * resta sulla pagina di login. Vedi `TokenStorageService`.
     *
     * NON decide se il cookie di sessione del backend viene inviato: quello è un fatto del backend
     * (`authentication.token-in-cookie`), e confonderlo con questa chiave è stata una trappola.
     */
    storeAccessToken: "sessionstorage" | "localstorage" | "cookies";
    csrfTokenCookieName: string;
    loginType: "local" | "saml-sso";
    loginUrl: string;
    checkTokenValidityBeforeExpireMin: number;
    loggerLevel: 0 | 1 | 2 | 3 | 4 | 5;
    baseHref: string;
    flickeringServicesResponse: number;
    /**
     * Numero massimo di organizzazioni e aree caricati per richiesta nel form impianti
     * (getOrganizations e getAreas). Valori per ambiente nei JSON sotto assets/config
     * (common-config più dev, staging o prod). Riferimento default in common-config.json.
     * Oltre questo limite i dati restano tronchi fino a eventuale paginazione lazy sul componente.
     */
    facilityFormDropdownMaxRecords?: number;
}

@Injectable()
export class EnvironmentLoaderService {

    private envConfig!: GeneralConfig;
    private configPath = "";
    private commonPath = "";
    constructor() { }
    /**
    * Carica il file di configurazione dell'applicazione
    * Nel caso in cui un parametro sia presente sia nelle configurazioni comuni che in quelle dedicate,
    * il parametro presente nelle dedicate sovrasciverà il precedente parametro.
    */
    public async setAppConfig(): Promise<void> {
        /** Controlla l'indirizzo dell'uri per definire l'ambiente da caricare */
        const locatioURI: string = window.location.toString();

        /** In base all'uri recupera il path dell'ambiente corretto */
        this.setDirectoryBasedOnURI(locatioURI);

        /** Setta i dati di configurazione che verranno poi caricati al caricamento dell'applicazione */
        try {
            const commonConfig = await this.loadCommonConfig();
            const dedicatedConfig = await this.loadDedicatedConfig();
            this.envConfig = this.overwriteCommonWithDedicated(commonConfig, dedicatedConfig);
        } catch (err: unknown) {
            // do nothing
        }
    }
    /**
     *
     * @async Carica i dati di configurazione comuni ai vari ambienti
     */
    private async loadCommonConfig(): Promise<GeneralConfig> {
        try {
            const commonConfigFile: Response = await fetch(this.commonPath);
            return await commonConfigFile.json() as GeneralConfig;
        } catch (err: unknown) {
            return {} as GeneralConfig;
        }
    }
    /**
     *
     * @async Carica i dati di configurazione dedicati
     */
    private async loadDedicatedConfig(): Promise<GeneralConfig> {
        try {
            const dedicatedConfigFile: Response = await fetch(this.configPath);
            return await dedicatedConfigFile.json() as GeneralConfig;
        } catch (err: unknown) {
            return {} as GeneralConfig;
        }
    }
    /**
     *
     * @returns Variabili d'ambiente caricate prima dell'inizializzazione dell'applicazione
     */
    public getEnvConfig(): GeneralConfig {
        return this.envConfig;
    }
    /**
     *
     * In base alla URI, carica automaticamente la configurazione corretta
     *
     * @param location URI dell'applicazione
     */
    private setDirectoryBasedOnURI(location: string): void {
        if (
            location.match(/https:\/\/dev-.*\.alloy\.it/)
            ?? location.match(/^http:\/\/([a-z0-9]+\.)?localhost(:\d+)?(\/|$)/i)) {
            this.configPath = env.configurationDevPath;
        } else if (location.match(/https:\/\/test-.*\.alloy\.it/)) {
            this.configPath = env.configurationStagingPath;
        } else if (env.production) {
            this.configPath = env.configurationProdPath;
        } else {
            throw new Error("Matching environment not found for URI: " + location);
        }
        this.commonPath = env.commonConfiguration;
    }

    /**
    *
    * @param commonConfig Configurazione comune ai vari ambienti
    * @param dedicatedConfig Configurazione dedicata ai vari ambienti
    * @returns Unione delle 2 configurazioni dove la dedicata sovrascrive i valori della comune se presenti
    */
    /* eslint-disable */
    private overwriteCommonWithDedicated(commonConfig: any, dedicatedConfig: any): GeneralConfig {
        /** Creazione di una copia della config comune */
        const merged = { ...commonConfig };

        for (const key of Object.keys(dedicatedConfig)) {
            /** Controllo che l'elemento corrente sia un un oggetto  */
            if (typeof dedicatedConfig[key] !== "number" && typeof dedicatedConfig[key] !== "boolean" && typeof dedicatedConfig[key] !== "string" && !Array.isArray(dedicatedConfig[key])) {
                /** Ricorsione in caso di oggetti annidati  */
                merged[key] = this.overwriteCommonWithDedicated(merged[key], dedicatedConfig[key]);
            } else {
                /**
                 * Inserisco il valore nel merged.
                 * Se il valore già esiste viene sovrascritto, altrimenti viene inserito
                 */
                merged[key] = dedicatedConfig[key];
            }
        }

        return merged;
    }
    /* eslint-enable */

}
